<template>
  <div class="min-h-screen flex flex-col">
    <NavBar :title="''" />

    <section class="flex-1 bg-gradient-to-br from-blue-500 to-slate-800 pt-16 pb-24">
      <div class="max-w-3xl mx-auto px-4 py-4">

        <!-- Header Card -->
        <div class="bg-white/95 backdrop-blur-lg rounded-2xl p-4 md:p-6 mb-4 shadow-xl">
          <div class="flex items-center gap-3">
            <div class="w-12 h-12 bg-amber-100 text-amber-600 rounded-xl flex items-center justify-center text-xl">
              <i class="fas fa-warehouse"></i>
            </div>
            <div>
              <h2 class="text-lg font-bold text-gray-800">Warehouse Delivery</h2>
              <p class="text-sm text-gray-500">Deliver collected UCO to warehouse</p>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="bg-white/95 backdrop-blur-lg rounded-xl p-8">
          <div class="flex flex-col items-center gap-4">
            <div class="w-10 h-10 border-3 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
            <p class="text-gray-500">Loading delivery history...</p>
          </div>
        </div>

        <template v-else>

          <!-- Delivery Success -->
          <div v-if="deliverySuccess" class="space-y-4">
            <div class="bg-white/95 backdrop-blur-lg rounded-2xl p-6 shadow-xl text-center">
              <div class="text-6xl text-green-600 mb-4 animate-scale-in">
                <i class="fas fa-check-circle"></i>
              </div>
              <h3 class="text-xl font-bold text-gray-800 mb-4">Delivery Confirmed!</h3>

              <div v-for="result in deliveryResults" :key="result.routeId" class="bg-green-50 border border-green-200 rounded-xl p-4 mb-3 text-left">
                <div class="flex items-center gap-2 mb-2">
                  <i class="fas fa-route text-green-600"></i>
                  <span class="text-sm font-semibold text-green-800">{{ result.routeId }}</span>
                </div>
                <div class="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span class="text-green-600">POs Received</span>
                    <span class="block font-bold text-green-800">{{ result.collectionsReceived }} / {{ result.totalCollections }}</span>
                  </div>
                  <div>
                    <span class="text-green-600">Total Volume</span>
                    <span class="block font-bold text-green-800">{{ result.totalVolume }}L</span>
                  </div>
                </div>
                <p class="text-xs text-green-600 mt-2">
                  <i class="fas fa-user-check mr-1"></i>Received by {{ result.receivedByEmployeeName }}
                </p>
              </div>

              <button
                @click="resetView"
                class="w-full h-12 flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold rounded-xl mt-4 transition-all active:scale-[0.98]"
              >
                <i class="fas fa-redo"></i>
                <span>Done</span>
              </button>
            </div>
          </div>

          <!-- Main Flow -->
          <template v-else>

            <!-- No Pending Deliveries -->
            <div v-if="pendingOrders.length === 0" class="bg-white/95 backdrop-blur-lg rounded-xl p-8 text-center">
              <div class="text-5xl text-gray-300 mb-4">
                <i class="fas fa-inbox"></i>
              </div>
              <p class="font-semibold text-gray-600 mb-1">No pending deliveries</p>
              <p class="text-sm text-gray-400">Complete collections on your route first</p>
              <button
                @click="fetchDeliveryHistory"
                class="mt-5 px-6 h-11 inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-600 font-medium rounded-xl transition-colors"
              >
                <i class="fas fa-sync-alt"></i>
                Refresh
              </button>
            </div>

            <!-- Pending Orders -->
            <template v-else>
              <!-- Orders List -->
              <div class="mb-4">
                <h3 class="text-white text-sm font-semibold mb-2 flex items-center gap-2">
                  <i class="fas fa-boxes"></i>
                  Pending Collections ({{ pendingOrders.length }})
                </h3>
                <div class="bg-white/95 backdrop-blur-lg rounded-xl p-4 shadow-xl">
                  <ul class="divide-y divide-gray-100">
                    <li v-for="order in pendingOrders" :key="order.collectionRequestId" class="py-3 first:pt-0 last:pb-0">
                      <div class="flex items-center justify-between gap-3">
                        <div class="flex-1 min-w-0">
                          <span class="block text-gray-800 font-medium truncate">{{ order.supplierName }}</span>
                          <span v-if="order.locationName" class="block text-xs text-gray-400 truncate">{{ order.locationName }}</span>
                          <span class="text-xs text-gray-400">{{ formatDate(order.scheduledDate) }}</span>
                        </div>
                        <div class="flex flex-col items-end gap-0.5 flex-shrink-0">
                          <span class="text-sm font-bold text-blue-600">{{ order.volume }}L</span>
                          <span class="text-xs text-gray-400">PO #{{ order.odooPurchaseOrderId }}</span>
                        </div>
                      </div>
                    </li>
                  </ul>

                  <!-- Total -->
                  <div class="mt-3 pt-3 border-t-2 border-gray-100 flex items-center justify-between">
                    <span class="font-semibold text-gray-700">Total Volume</span>
                    <span class="text-xl font-bold text-blue-600">{{ totalVolume }}L</span>
                  </div>
                </div>
              </div>

              <!-- Employee Verification Section -->
              <div class="mb-4">
                <h3 class="text-white text-sm font-semibold mb-2 flex items-center gap-2">
                  <i class="fas fa-user-check"></i>
                  Warehouse Employee Verification
                </h3>
                <div class="bg-white/95 backdrop-blur-lg rounded-xl p-4 shadow-xl">

                  <!-- Employee Dropdown -->
                  <div class="mb-4">
                    <label class="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                      <i class="fas fa-id-card text-gray-400"></i> Select Employee
                    </label>
                    <div v-if="loadingEmployees" class="flex items-center gap-2 text-gray-400 text-sm py-3">
                      <div class="w-4 h-4 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
                      Loading employees...
                    </div>
                    <select
                      v-else
                      v-model="selectedEmployeeId"
                      class="w-full h-12 px-3 text-base border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 outline-none transition-all"
                    >
                      <option value="" disabled>Choose warehouse employee...</option>
                      <option v-for="emp in employees" :key="emp.id" :value="emp.id">{{ emp.name }}</option>
                    </select>
                  </div>

                  <!-- PIN Input -->
                  <div class="mb-5">
                    <label class="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                      <i class="fas fa-lock text-gray-400"></i> Employee PIN
                    </label>
                    <input
                      v-model="pin"
                      type="password"
                      inputmode="numeric"
                      placeholder="Enter PIN"
                      maxlength="10"
                      class="w-full h-12 px-4 text-base text-center tracking-[0.3em] font-mono border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 outline-none transition-all"
                    />
                  </div>

                  <!-- Error -->
                  <transition name="fade">
                    <div v-if="deliveryError" class="flex items-start gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                      <i class="fas fa-exclamation-triangle mt-0.5"></i>
                      <span>{{ deliveryError }}</span>
                    </div>
                  </transition>

                  <!-- Confirm Button -->
                  <button
                    :disabled="!canDeliver || delivering"
                    class="w-full h-14 flex items-center justify-center gap-2 bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold rounded-xl shadow-lg shadow-green-500/30 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                    @click="confirmDelivery"
                  >
                    <span v-if="delivering" class="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                    <template v-else>
                      <i class="fas fa-check-double"></i>
                      <span>Confirm Delivery</span>
                    </template>
                  </button>
                </div>
              </div>
            </template>
          </template>
        </template>
      </div>
    </section>
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import NavBar from "@/components/NavBar.vue";
import { useStore } from "vuex";

import { authHeaders, forceReLogin } from "@/composables/apiAuth";

const fetchFromAPI = async (endpoint, payload) => {
  const response = await fetch(endpoint, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  if (response.status === 401) {
    forceReLogin();
  }
  return response.json();
};

export default {
  name: "DeliverView",
  components: {
    NavBar,
  },
  setup() {
    const store = useStore();
    const userString = sessionStorage.getItem("user");
    const truckNumber = sessionStorage.getItem("truckNumber");
    const user = JSON.parse(userString);
    const siteId = user.siteid;

    // State
    const loading = ref(true);
    const loadingEmployees = ref(true);
    const delivering = ref(false);
    const pendingOrders = ref([]);
    const employees = ref([]);
    const selectedEmployeeId = ref("");
    const pin = ref("");
    const deliveryError = ref(null);
    const deliverySuccess = ref(false);
    const deliveryResults = ref([]);

    // Computed
    const totalVolume = computed(() => {
      return pendingOrders.value.reduce((sum, o) => sum + (o.volume || 0), 0);
    });

    const canDeliver = computed(() => {
      return selectedEmployeeId.value && pin.value.trim().length > 0 && pendingOrders.value.length > 0;
    });

    // Get unique routeIds from pending orders
    const uniqueRouteIds = computed(() => {
      return [...new Set(pendingOrders.value.map((o) => o.routeId))];
    });

    // API: Fetch delivery history
    const fetchDeliveryHistory = async () => {
      loading.value = true;
      try {
        const data = await fetchFromAPI(store.state.api + "getDeliveryHistory", {
          truckNumber,
          siteId,
        });

        if (data && data.orders) {
          pendingOrders.value = data.orders;
        } else if (Array.isArray(data) && data[2] === 200 && data[1]?.orders) {
          pendingOrders.value = data[1].orders;
        } else {
          pendingOrders.value = [];
        }
      } catch (err) {
        console.error("Error fetching delivery history:", err);
        pendingOrders.value = [];
      } finally {
        loading.value = false;
      }
    };

    // API: Fetch manufacturing employees
    const fetchEmployees = async () => {
      loadingEmployees.value = true;
      try {
        const data = await fetchFromAPI(store.state.api + "getManufacturingEmployees", {
          truckNumber,
          siteId,
        });

        if (Array.isArray(data) && data[0] === "Complete" && data[1]?.employees) {
          employees.value = data[1].employees;
        } else if (data && data.employees) {
          employees.value = data.employees;
        } else {
          employees.value = [];
        }
      } catch (err) {
        console.error("Error fetching employees:", err);
        employees.value = [];
      } finally {
        loadingEmployees.value = false;
      }
    };

    // Geolocation
    const getLocationOrDefault = async () => {
      try {
        return await new Promise((resolve, reject) => {
          if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
              (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
              (err) => reject(err)
            );
          } else {
            reject(new Error("Geolocation not supported"));
          }
        });
      } catch {
        return { lat: 0, lng: 0 };
      }
    };

    // Confirm delivery - calls deliverRoute for each unique routeId
    const confirmDelivery = async () => {
      if (!canDeliver.value) return;
      delivering.value = true;
      deliveryError.value = null;

      try {
        const loc = await getLocationOrDefault();
        const results = [];

        for (const routeId of uniqueRouteIds.value) {
          const data = await fetchFromAPI(store.state.api + "deliverRoute", {
            routeId,
            truckNumber,
            siteId,
            employeeId: selectedEmployeeId.value,
            pin: pin.value,
            lat: loc.lat,
            lng: loc.lng,
          });

          // Handle error responses
          if (data?.detail === "Invalid employee PIN.") {
            deliveryError.value = "Invalid employee PIN. Please check and try again.";
            return;
          }

          if (Array.isArray(data)) {
            if (data[2] === 401) {
              deliveryError.value = "Invalid employee PIN. Please check and try again.";
              return;
            }
            if (data[2] === 400) {
              deliveryError.value = data[1]?.message || "No completed collections to deliver.";
              return;
            }
            if (data[0] === "Complete" && data[2] === 200) {
              results.push({ routeId, ...data[1].delivery });
            } else {
              deliveryError.value = data[1]?.message || "Delivery failed. Please try again.";
              return;
            }
          } else {
            deliveryError.value = "Unexpected response from server.";
            return;
          }
        }

        deliveryResults.value = results;
        deliverySuccess.value = true;
      } catch (err) {
        console.error("Error confirming delivery:", err);
        deliveryError.value = "An error occurred during delivery. Please try again.";
      } finally {
        delivering.value = false;
      }
    };

    // Reset to initial state
    const resetView = () => {
      deliverySuccess.value = false;
      deliveryResults.value = [];
      selectedEmployeeId.value = "";
      pin.value = "";
      deliveryError.value = null;
      fetchDeliveryHistory();
    };

    // Helpers
    const formatDate = (isoString) => {
      if (!isoString) return "";
      const date = new Date(isoString);
      return date.toLocaleDateString("en-AE", { weekday: "short", day: "numeric", month: "short" });
    };

    // Init
    onMounted(() => {
      fetchDeliveryHistory();
      fetchEmployees();
    });

    return {
      loading,
      loadingEmployees,
      delivering,
      pendingOrders,
      employees,
      selectedEmployeeId,
      pin,
      totalVolume,
      canDeliver,
      deliveryError,
      deliverySuccess,
      deliveryResults,
      fetchDeliveryHistory,
      confirmDelivery,
      resetView,
      formatDate,
    };
  },
};
</script>

<style scoped>
.animate-scale-in {
  animation: scaleIn 0.5s ease;
}

@keyframes scaleIn {
  from {
    transform: scale(0);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
